import { useState, useEffect } from "react";
import { adminFetch } from "../../lib/api";
import type { MonitorStatus, PodSignal, UrlSignal } from "../../types/monitoring";

// ── Badge helpers ──────────────────────────────────────────────────────────────

function HealthBadge({ healthy, failures }: { healthy: boolean | null; failures: number }) {
  if (healthy === null) return <span className="ac-badge ac-badge--neutral">Pending</span>;
  if (healthy) return <span className="ac-badge ac-badge--operational">Healthy</span>;
  if (failures > 0) return <span className="ac-badge ac-badge--outage">Unhealthy ({failures}&#xd7;)</span>;
  return <span className="ac-badge ac-badge--degraded">Degraded</span>;
}

function TypeBadge({ type }: { type: string }) {
  return type === "aks_pod"
    ? <span className="ac-badge ac-badge--info">AKS Pod</span>
    : <span className="ac-badge ac-badge--neutral">HTTP</span>;
}

const POD_SIGNAL_LABEL: Record<PodSignal, string> = {
  GREEN: "All Ready",
  ORANGE_PARTIAL: "Partial",
  ORANGE_PENDING: "Pending",
  RED: "None/Crash",
  "": "—",
};

const POD_SIGNAL_CLASS: Record<PodSignal, string> = {
  GREEN: "ac-badge ac-badge--operational",
  ORANGE_PARTIAL: "ac-badge ac-badge--degraded",
  ORANGE_PENDING: "ac-badge ac-badge--maintenance",
  RED: "ac-badge ac-badge--outage",
  "": "ac-badge ac-badge--neutral",
};

const URL_SIGNAL_CLASS: Record<UrlSignal, string> = {
  GREEN: "ac-badge ac-badge--operational",
  ORANGE: "ac-badge ac-badge--degraded",
  RED: "ac-badge ac-badge--outage",
  "": "ac-badge ac-badge--neutral",
};

function PodSignalBadge({ signal }: { signal: PodSignal }) {
  return <span className={POD_SIGNAL_CLASS[signal]}>{POD_SIGNAL_LABEL[signal]}</span>;
}

function UrlSignalBadge({ signal }: { signal: UrlSignal }) {
  if (!signal) return <span style={{ color: "var(--aptean-text-secondary)", fontSize: "0.75rem" }}>N/A</span>;
  return <span className={URL_SIGNAL_CLASS[signal]}>{signal}</span>;
}

function AksMatrixCell({ e }: { e: MonitorStatus }) {
  const podReady = e.aks_pod_ready;
  const podTotal = e.aks_pod_total;
  const counts = podTotal !== null ? `${podReady ?? "?"}/${podTotal}` : null;

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: "0.25rem" }}>
      <div style={{ display: "flex", alignItems: "center", gap: "0.375rem" }}>
        <span style={{ fontSize: "0.7rem", color: "var(--aptean-text-secondary)", minWidth: "28px" }}>Pods</span>
        <PodSignalBadge signal={e.aks_pod_signal} />
        {counts && <span style={{ fontSize: "0.75rem", color: "var(--aptean-text-secondary)" }}>{counts}</span>}
      </div>
      <div style={{ display: "flex", alignItems: "center", gap: "0.375rem" }}>
        <span style={{ fontSize: "0.7rem", color: "var(--aptean-text-secondary)", minWidth: "28px" }}>URL</span>
        <UrlSignalBadge signal={e.aks_url_signal} />
      </div>
    </div>
  );
}

// ── Decision matrix tooltip / legend ──────────────────────────────────────────

function MatrixLegend() {
  const [open, setOpen] = useState(false);
  return (
    <span style={{ position: "relative", display: "inline-block" }}>
      <button
        type="button"
        onClick={() => setOpen(o => !o)}
        style={{ background: "none", border: "none", cursor: "pointer", fontSize: "0.75rem", color: "var(--aptean-teal, #54B3BE)", textDecoration: "underline" }}
      >
        Decision matrix
      </button>
      {open && (
        <div style={{
          position: "absolute", top: "calc(100% + 4px)", right: 0, zIndex: 100,
          background: "var(--aptean-bg-surface)", border: "1px solid var(--aptean-border)",
          borderRadius: "8px", padding: "0.875rem", width: "420px", boxShadow: "0 4px 16px rgba(0,0,0,0.2)",
          fontSize: "0.8125rem",
        }}>
          <div style={{ fontWeight: 700, marginBottom: "0.625rem" }}>AKS Health Decision Logic</div>
          <table style={{ width: "100%", borderCollapse: "collapse" }}>
            <thead>
              <tr style={{ borderBottom: "1px solid var(--aptean-border)" }}>
                <th style={{ textAlign: "left", padding: "0.25rem 0.5rem", color: "var(--aptean-text-secondary)", fontWeight: 600 }}>Pod Signal</th>
                <th style={{ textAlign: "left", padding: "0.25rem 0.5rem", color: "var(--aptean-text-secondary)", fontWeight: 600 }}>URL Signal</th>
                <th style={{ textAlign: "left", padding: "0.25rem 0.5rem", color: "var(--aptean-text-secondary)", fontWeight: 600 }}>Final Status</th>
              </tr>
            </thead>
            <tbody>
              {[
                ["All Ready (100%)", "HTTP 200", "operational", "Operational"],
                ["All Ready (100%)", "Non-2xx", "degraded", "Degraded"],
                ["Partial Ready", "HTTP 200", "degraded", "Degraded"],
                ["Partial Ready", "Non-2xx", "outage", "Outage"],
                ["No pods (0)", "Any", "outage", "Outage"],
                ["CrashLoopBackOff", "Any", "outage", "Outage"],
                ["Pending > N min", "Any", "degraded", "Degraded (capped)"],
              ].map(([pod, url, cls, label]) => (
                <tr key={pod + url} style={{ borderBottom: "1px solid var(--aptean-border)" }}>
                  <td style={{ padding: "0.3rem 0.5rem" }}>{pod}</td>
                  <td style={{ padding: "0.3rem 0.5rem" }}>{url}</td>
                  <td style={{ padding: "0.3rem 0.5rem" }}><span className={`ac-badge ac-badge--${cls}`}>{label}</span></td>
                </tr>
              ))}
            </tbody>
          </table>
          <div style={{ marginTop: "0.625rem", fontSize: "0.75rem", color: "var(--aptean-text-secondary)" }}>
            Rule priority: RED &gt; ORANGE &gt; GREEN. Single failed pod &ne; Outage.
          </div>
          <button type="button" onClick={() => setOpen(false)} style={{ marginTop: "0.5rem", fontSize: "0.75rem", color: "var(--aptean-teal, #54B3BE)", background: "none", border: "none", cursor: "pointer" }}>Close</button>
        </div>
      )}
    </span>
  );
}

// ── Main page ─────────────────────────────────────────────────────────────────

export function MonitorDashboardPage() {
  const [entries, setEntries] = useState<MonitorStatus[]>([]);
  const [loading, setLoading] = useState(true);
  const [lastRefresh, setLastRefresh] = useState<Date | null>(null);

  async function load() {
    const r = await adminFetch("/api/v1/admin/monitor-status/");
    if (r.ok) { setEntries(await r.json()); setLastRefresh(new Date()); }
    setLoading(false);
  }

  useEffect(() => { load(); const id = setInterval(load, 15_000); return () => clearInterval(id); }, []);

  const healthy = entries.filter(e => e.is_healthy === true).length;
  const unhealthy = entries.filter(e => e.is_healthy === false).length;
  const httpCount = entries.filter(e => e.monitor_type === "http").length;
  const aksCount = entries.filter(e => e.monitor_type === "aks_pod").length;

  // AKS-specific summaries
  const aksOutage = entries.filter(e => e.monitor_type === "aks_pod" && e.aks_combined_status === "outage").length;
  const aksDegraded = entries.filter(e => e.monitor_type === "aks_pod" && e.aks_combined_status === "degraded").length;

  return (
    <div className="ac-page">
      <div className="ac-page-header">
        <div className="ac-page-header__left">
          <h1 className="ac-page-title">Monitor Dashboard</h1>
          <p className="ac-page-subtitle">
            Real-time endpoint health &mdash; auto-refreshes every 15s
            {lastRefresh && <span style={{ marginLeft: "0.5rem" }}>&middot; Last updated {lastRefresh.toLocaleTimeString()}</span>}
            {aksCount > 0 && <span style={{ marginLeft: "1rem" }}><MatrixLegend /></span>}
          </p>
        </div>
        <div className="ac-page-header__actions">
          <button type="button" className="aptean-btn aptean-btn--secondary" onClick={load}>Refresh</button>
        </div>
      </div>

      <div className="ac-summary-strip">
        <div className="ac-summary-card">
          <div className="ac-summary-card__value">{entries.length}</div>
          <div className="ac-summary-card__label">Monitors</div>
        </div>
        <div className={`ac-summary-card${healthy === entries.length && entries.length > 0 ? " ac-summary-card--ok" : ""}`}>
          <div className="ac-summary-card__value">{healthy}</div>
          <div className="ac-summary-card__label">Healthy</div>
        </div>
        <div className={`ac-summary-card${unhealthy > 0 ? " ac-summary-card--danger" : ""}`}>
          <div className="ac-summary-card__value">{unhealthy}</div>
          <div className="ac-summary-card__label">Unhealthy</div>
        </div>
        <div className="ac-summary-card">
          <div className="ac-summary-card__value">{httpCount}</div>
          <div className="ac-summary-card__label">HTTP</div>
        </div>
        {aksCount > 0 && (
          <>
            <div className="ac-summary-card">
              <div className="ac-summary-card__value">{aksCount}</div>
              <div className="ac-summary-card__label">AKS Pod</div>
            </div>
            {aksOutage > 0 && (
              <div className="ac-summary-card ac-summary-card--danger">
                <div className="ac-summary-card__value">{aksOutage}</div>
                <div className="ac-summary-card__label">AKS Outage</div>
              </div>
            )}
            {aksDegraded > 0 && (
              <div className="ac-summary-card ac-summary-card--warn">
                <div className="ac-summary-card__value">{aksDegraded}</div>
                <div className="ac-summary-card__label">AKS Degraded</div>
              </div>
            )}
          </>
        )}
      </div>

      {loading ? (
        <div style={{ padding: "3rem", textAlign: "center", color: "var(--aptean-text-secondary)" }}>Loading...</div>
      ) : (
        <div className="ac-table-wrap">
          <table className="ac-table">
            <thead>
              <tr>
                <th style={{ width: "16%" }}>Service</th>
                <th style={{ width: "11%" }}>Region</th>
                <th style={{ width: "7%" }}>Type</th>
                <th>Target</th>
                <th>Health</th>
                <th style={{ width: "18%" }}>Pod / URL Signals</th>
                <th>HTTP / Pods</th>
                <th>Resp</th>
                <th>Fails</th>
                <th>Checked</th>
              </tr>
            </thead>
            <tbody>
              {entries.length === 0 ? (
                <tr><td colSpan={10}>
                  <div className="ac-empty">
                    <div className="ac-empty__title">No monitors configured</div>
                    <div className="ac-empty__body">Go to Service Registry &#8594; Endpoints &#8594; + Add Monitor</div>
                  </div>
                </td></tr>
              ) : entries.map(e => {
                const isAks = e.monitor_type === "aks_pod";
                const rowStyle = e.is_healthy === false
                  ? { borderLeft: `3px solid ${e.aks_combined_status === "outage" ? "var(--aptean-red, #a4262c)" : "var(--aptean-orange, #d9730d)"}` }
                  : {};
                return (
                  <tr key={e.endpoint_id} style={rowStyle}>
                    <td className="ac-row-title">{e.service_name}</td>
                    <td className="ac-td--muted">{e.region_name}</td>
                    <td><TypeBadge type={e.monitor_type} /></td>
                    <td
                      className="ac-td--mono"
                      style={{ fontSize: "0.8125rem", maxWidth: "200px", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}
                      title={isAks ? `${e.aks_namespace} / ${e.aks_label_selector}` : e.url}
                    >
                      {isAks
                        ? <><span style={{ color: "var(--aptean-text-secondary)" }}>{e.aks_namespace}/</span>{e.aks_label_selector}</>
                        : e.url}
                    </td>
                    <td><HealthBadge healthy={e.is_healthy} failures={e.consecutive_failures} /></td>
                    <td>
                      {isAks
                        ? <AksMatrixCell e={e} />
                        : <span style={{ color: "var(--aptean-text-secondary)", fontSize: "0.75rem" }}>—</span>}
                    </td>
                    <td className="ac-td--muted">
                      {isAks
                        ? (e.aks_pod_ready !== null ? `${e.aks_pod_ready}/${e.aks_pod_total} ready` : "—")
                        : (e.last_http_status ?? "—")}
                    </td>
                    <td className="ac-td--muted">
                      {isAks ? "—" : (e.last_response_ms !== null ? `${e.last_response_ms}ms` : "—")}
                    </td>
                    <td className="ac-td--muted">{e.consecutive_failures}</td>
                    <td className="ac-td--muted">{e.last_checked_at ? new Date(e.last_checked_at).toLocaleTimeString() : "—"}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>

          {/* AKS detail panel — shown when any AKS check has a detail message */}
          {entries.some(e => e.monitor_type === "aks_pod" && e.aks_detail) && (
            <div style={{ padding: "0.875rem 1rem", borderTop: "1px solid var(--aptean-border)" }}>
              <div style={{ fontSize: "0.75rem", fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.06em", color: "var(--aptean-text-secondary)", marginBottom: "0.5rem" }}>
                AKS Check Details
              </div>
              {entries.filter(e => e.monitor_type === "aks_pod" && e.aks_detail).map(e => {
                const statusCls = e.aks_combined_status === "outage"
                  ? "ac-badge--outage"
                  : e.aks_combined_status === "degraded"
                  ? "ac-badge--degraded"
                  : "ac-badge--operational";
                return (
                  <div key={e.endpoint_id} style={{ display: "flex", alignItems: "flex-start", gap: "0.625rem", marginBottom: "0.5rem", fontSize: "0.8125rem" }}>
                    <span className={`ac-badge ${statusCls}`} style={{ flexShrink: 0 }}>{e.aks_combined_status ?? "—"}</span>
                    <span style={{ color: "var(--aptean-text-primary)", fontWeight: 600, flexShrink: 0 }}>{e.service_name} / {e.region_name}:</span>
                    <span style={{ color: "var(--aptean-text-secondary)" }}>{e.aks_detail}</span>
                  </div>
                );
              })}
            </div>
          )}

          {/* HTTP error panel */}
          {entries.some(e => e.monitor_type === "http" && e.is_healthy === false && e.last_error) && (
            <div style={{ padding: "0.875rem 1rem", borderTop: "1px solid var(--aptean-border)" }}>
              <div style={{ fontSize: "0.75rem", fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.06em", color: "var(--aptean-text-secondary)", marginBottom: "0.5rem" }}>
                HTTP Check Errors
              </div>
              {entries.filter(e => e.monitor_type === "http" && e.is_healthy === false && e.last_error).map(e => (
                <div key={e.endpoint_id} style={{ fontSize: "0.8125rem", color: "var(--aptean-text-secondary)", marginBottom: "0.25rem" }}>
                  <strong style={{ color: "var(--aptean-text-primary)" }}>{e.service_name} / {e.region_name}:</strong>{" "}
                  {e.last_error}
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
